// Vitals section (step 1) for VisitReportPage.
// Conditional context fields shown when a vital is taken; not-taken toggle + reason picker per vital.

import { useRef, type JSX } from "react";
import { FormField } from "./FormField.js";
import { SelectField } from "./SelectField.js";
import { SwitchToggle } from "./SwitchToggle.js";
import type { FormState, Action } from "../lib/visit-report-types.js";
import { SECTION, HEADING } from "../lib/visit-report-types.js";
import { NOT_TAKEN_REASON_OPTIONS, type SubmitInput } from "@hcm/core";
import type { SectionAutoSaveBus } from "./VisitReportWizard.js";
import { fieldErrorMessage, type SectionFieldErrors } from "../lib/visit-report-section-validators.js";
import { useFocusOnApiField } from "../hooks/use-focus-on-api-field.js";

const OPTIONAL_LABEL = "— optional —";

const NT_OPTS = [
  { value: "", label: "— select reason —" },
  ...NOT_TAKEN_REASON_OPTIONS.map((r) => ({ value: r.value, label: r.label })),
];
const BP_POS = [
  { value: "", label: OPTIONAL_LABEL },
  { value: "sitting",  label: "Sitting" },
  { value: "lying",    label: "Lying" },
  { value: "standing", label: "Standing" },
];
const HR_RHYTHM = [
  { value: "", label: OPTIONAL_LABEL },
  { value: "regular",          label: "Regular" },
  { value: "irregular",        label: "Irregular" },
  { value: "unable_to_assess", label: "Unable to assess" },
];
const GLUCOSE_TIMING = [
  { value: "", label: OPTIONAL_LABEL },
  { value: "fasting",       label: "Fasting" },
  { value: "post_meal_1hr", label: "Post-meal 1 hr" },
  { value: "post_meal_2hr", label: "Post-meal 2 hr" },
  { value: "random",        label: "Random" },
];
const ANKLE_OEDEMA = [
  { value: "",         label: "— select —" },
  { value: "none",     label: "None" },
  { value: "mild",     label: "Mild" },
  { value: "moderate", label: "Moderate" },
  { value: "severe",   label: "Severe" },
];

type ErrLookup = (api: string) => string | undefined;

interface NotTakenRowProps {
  label:           string;
  toggleLabel:     string;
  notTaken:        boolean;
  reasonValue:     string;
  // TASK-48 S2 — typed against the canonical schema; renaming a
  // <vital>_not_taken_reason field will fail tsc here at every literal
  // call-site, closing the rr/resp drift bug class.
  reasonApiField:  keyof SubmitInput;
  onToggle:        (on: boolean) => void;
  onReasonChange:  (v: string) => void;
  err:             ErrLookup;
}

/** Common chrome around every vital: not-taken toggle + reason picker (when toggled). */
function NotTakenRow(p: NotTakenRowProps): JSX.Element {
  return (
    <>
      <div className="flex items-center gap-4 mb-2">
        <SwitchToggle label={p.toggleLabel} checked={p.notTaken} onChange={p.onToggle} />
      </div>
      {p.notTaken && (
        <div className="mb-3">
          <SelectField label={p.label} value={p.reasonValue}
            onChange={p.onReasonChange} options={NT_OPTS} error={p.err(p.reasonApiField)}
            apiFieldName={p.reasonApiField} />
        </div>
      )}
    </>
  );
}

interface NumProps { onValueChange?: () => void; onBlur?: () => void; }
type Fn = (field: Action["field"]) => (v: string) => void;
type Nt = (field: Action["field"]) => (on: boolean) => void;

interface BlockArgs { f: FormState; fn: Fn; nt: Nt; numProps: NumProps; err: ErrLookup; }

function BPBlock({ f, fn, nt, numProps, err }: BlockArgs): JSX.Element {
  return (
    <>
      {f.bp_not_taken !== "yes" && (
        <div className="grid grid-cols-2 gap-3 mb-1">
          <FormField label="BP Systolic (mmHg)"  type="number" value={f.bp_sys} onChange={fn("bp_sys")} error={err("bp_systolic")}  apiFieldName="bp_systolic"  {...numProps} />
          <FormField label="BP Diastolic (mmHg)" type="number" value={f.bp_dia} onChange={fn("bp_dia")} error={err("bp_diastolic")} apiFieldName="bp_diastolic" {...numProps} />
          {f.bp_sys && (
            <div className="col-span-2">
              <SelectField label="BP position" value={f.bp_pos} onChange={fn("bp_pos")} options={BP_POS} error={err("bp_position")} apiFieldName="bp_position" required />
            </div>
          )}
        </div>
      )}
      <NotTakenRow label="BP — reason not taken" toggleLabel="BP not taken"
        notTaken={f.bp_not_taken === "yes"} reasonValue={f.bp_not_taken_reason} reasonApiField="bp_not_taken_reason"
        onToggle={nt("bp_not_taken")} onReasonChange={fn("bp_not_taken_reason")} err={err} />
    </>
  );
}

function HRBlock({ f, fn, nt, numProps, err }: BlockArgs): JSX.Element {
  return (
    <div className="mt-2">
      {f.hr_not_taken !== "yes" && (
        <div className="grid grid-cols-2 gap-3 mb-1">
          <FormField label="Heart Rate (bpm)" type="number" value={f.hr} onChange={fn("hr")} error={err("heart_rate")} apiFieldName="heart_rate" {...numProps} />
          {f.hr && <SelectField label="Heart rhythm" value={f.hr_rhythm} onChange={fn("hr_rhythm")} options={HR_RHYTHM} error={err("heart_rate_rhythm")} apiFieldName="heart_rate_rhythm" required />}
        </div>
      )}
      <NotTakenRow label="HR — reason not taken" toggleLabel="HR not taken"
        notTaken={f.hr_not_taken === "yes"} reasonValue={f.hr_not_taken_reason} reasonApiField="hr_not_taken_reason"
        onToggle={nt("hr_not_taken")} onReasonChange={fn("hr_not_taken_reason")} err={err} />
    </div>
  );
}

function GlucoseBlock({ f, fn, nt, numProps, err }: BlockArgs): JSX.Element {
  return (
    <div className="mt-2">
      {f.glucose_not_taken !== "yes" && (
        <div className="grid grid-cols-2 gap-3 mb-1">
          <FormField label="Blood Glucose (mmol/L)" type="number" value={f.glucose} onChange={fn("glucose")} error={err("blood_glucose_mmol")} apiFieldName="blood_glucose_mmol" {...numProps} />
          {f.glucose && <SelectField label="Glucose timing" value={f.glucose_timing} onChange={fn("glucose_timing")} options={GLUCOSE_TIMING} error={err("blood_glucose_timing")} apiFieldName="blood_glucose_timing" required />}
        </div>
      )}
      <NotTakenRow label="Glucose — reason not taken" toggleLabel="Glucose not taken"
        notTaken={f.glucose_not_taken === "yes"} reasonValue={f.glucose_not_taken_reason} reasonApiField="glucose_not_taken_reason"
        onToggle={nt("glucose_not_taken")} onReasonChange={fn("glucose_not_taken_reason")} err={err} />
    </div>
  );
}

interface SimpleBlockProps extends BlockArgs {
  inputLabel:   string;
  inputType?:   string;
  // TASK-48 S2 — both API field strings typed against the canonical
  // schema; the parent's render call-site (literal strings like
  // "respiratory_rate" / "resp_not_taken_reason") is checked at compile
  // time, closing the rr/resp drift bug class.
  apiField:     keyof SubmitInput;
  formField:    keyof FormState;
  notTakenField: keyof FormState;
  reasonField:   keyof FormState;
  apiReasonField: keyof SubmitInput;
  toggleLabel:  string;
  reasonLabel:  string;
}

function SimpleVitalBlock(p: SimpleBlockProps): JSX.Element {
  const notTakenYes = p.f[p.notTakenField] === "yes";
  return (
    <div className="mt-2">
      {!notTakenYes && (
        <FormField label={p.inputLabel} type={p.inputType ?? "number"}
          value={p.f[p.formField] as string} onChange={p.fn(p.formField as Action["field"])}
          error={p.err(p.apiField)} apiFieldName={p.apiField} {...p.numProps} />
      )}
      <NotTakenRow label={p.reasonLabel} toggleLabel={p.toggleLabel}
        notTaken={notTakenYes} reasonValue={p.f[p.reasonField] as string} reasonApiField={p.apiReasonField}
        onToggle={p.nt(p.notTakenField as Action["field"])} onReasonChange={p.fn(p.reasonField as Action["field"])}
        err={p.err} />
    </div>
  );
}

interface VitalsProps {
  f: FormState;
  dispatch: (a: Action) => void;
  bus?: SectionAutoSaveBus;
  /** Q9 S6 / D8 — server + client validation errors keyed by API field name. */
  fieldErrors?: SectionFieldErrors;
  /** Q9 S7 — when set, the named field is focused after this section renders. */
  focusFieldName?: string | null;
}

export function VitalsSection({ f, dispatch, bus, fieldErrors, focusFieldName }: VitalsProps): JSX.Element {
  const fn:       Fn = (field) => (v) => dispatch({ field, value: v } as Action);
  const nt:       Nt = (field) => (on) => dispatch({ field, value: on ? "yes" : "" } as Action);
  const numProps: NumProps = { onValueChange: bus?.onNumericChange, onBlur: bus?.onFieldBlur };
  const err:      ErrLookup = (api) => fieldErrorMessage(fieldErrors, api);
  const args: BlockArgs = { f, fn, nt, numProps, err };
  const containerRef = useRef<HTMLDivElement>(null);
  useFocusOnApiField(containerRef, focusFieldName);

  return (
    <div ref={containerRef} className={SECTION}>
      <p className={HEADING}>1 — Vitals</p>
      <BPBlock {...args} />
      <HRBlock {...args} />
      <SimpleVitalBlock {...args} inputLabel="SpO2 (%)" apiField="spo2_percent"
        formField="spo2" notTakenField="spo2_not_taken" reasonField="spo2_not_taken_reason"
        apiReasonField="spo2_not_taken_reason"
        toggleLabel="SpO2 not taken" reasonLabel="SpO2 — reason not taken" />
      <SimpleVitalBlock {...args} inputLabel="Temperature (°C)" apiField="temperature_celsius"
        formField="temp" notTakenField="temp_not_taken" reasonField="temp_not_taken_reason"
        apiReasonField="temp_not_taken_reason"
        toggleLabel="Temp not taken" reasonLabel="Temp — reason not taken" />
      <GlucoseBlock {...args} />
      <SimpleVitalBlock {...args} inputLabel="Weight (kg)" apiField="weight_kg"
        formField="weight" notTakenField="weight_not_taken" reasonField="weight_not_taken_reason"
        apiReasonField="weight_not_taken_reason"
        toggleLabel="Weight not taken" reasonLabel="Weight — reason not taken" />
      <SimpleVitalBlock {...args} inputLabel="Respiratory Rate (br/min)" apiField="respiratory_rate"
        formField="rr" notTakenField="rr_not_taken" reasonField="rr_not_taken_reason"
        apiReasonField="resp_not_taken_reason"
        toggleLabel="RR not taken" reasonLabel="RR — reason not taken" />
      <div className="mt-4 pt-3 border-t border-slate-100">
        <SelectField label="Ankle oedema" value={f.ankle_oedema} onChange={fn("ankle_oedema")}
          options={ANKLE_OEDEMA} error={err("bilateral_ankle_oedema")} apiFieldName="bilateral_ankle_oedema" />
        <p className="mt-1 text-xs text-slate-400 italic">Required for heart failure patients</p>
      </div>
    </div>
  );
}
